// src/components/CourseEnrolledRoute.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { useFuncion } from "../context/FuncionContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../context/AuthContext";

export default function CourseEnrolledRoute({ children }) {
  const { setFooterVisible } = useOutletContext() || {};
  const { codigoCurso } = useParams();
  const { currentRole, user } = useAuth();

  // 👇 ahora usamos fetchCourses
  const { resolveStudentCourseByCode, listMyCourses, fetchCourses } = useFuncion();

  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [error, setError] = useState("");

  const norm = (v) => String(v ?? "").toLowerCase().trim();

  // Mantén las funciones estables vía ref (evita re-run por cambio de referencia)
  const fnsRef = useRef({ resolveStudentCourseByCode, listMyCourses, fetchCourses });
  useEffect(() => {
    fnsRef.current = { resolveStudentCourseByCode, listMyCourses, fetchCourses };
  }, [resolveStudentCourseByCode, listMyCourses, fetchCourses]);

  useEffect(() => {
    if (!setFooterVisible) return;
    if (loading) setFooterVisible(false);
    return () => setFooterVisible(true);
  }, [loading, setFooterVisible]);

  // ✅ Admin de facultad: verifica que el curso esté en su scope usando fetchCourses()
  const canAccessAsFacultyAdmin = useCallback(async (codigo, { signal } = {}) => {
    if (typeof fnsRef.current.fetchCourses !== "function") return false;

    const code = norm(codigo);

    // Tip: si el token no trae admin_facultades, igual puede existir por fallback BD en backend.
    // Aquí solo lo usamos para un mensaje más claro si quieres.
    const hasAnyFacInToken = Array.isArray(user?.admin_facultades) && user.admin_facultades.length > 0;

    // Intento 1: pedir cursos “visibles para mi” con paginado grande (sin search en endpoint)
    // (Tu fetchCourses no tiene search; así que hacemos limit alto)
    const res = await fnsRef.current.fetchCourses({
      page: 1,
      limit: 500,         // 👈 ajusta si esperas más
      showAll: true,
      // facultad_id: opcional (si quieres forzar una facultad)
      // pero normalmente NO hace falta, porque backend scoping ya limita.
      signal,             // si tu authenticatedRequest lo soporta, perfecto; si no, se ignora.
    });

    const arr =
      Array.isArray(res?.items) ? res.items :
      Array.isArray(res?.courses) ? res.courses :
      Array.isArray(res?.cursos) ? res.cursos :
      Array.isArray(res) ? res : [];

    const found = arr.some((c) => {
      const ccode = norm(c?.codigo_curso ?? c?.codigoCurso ?? c?.codigo ?? c?.code);
      return ccode === code;
    });

    // Si no trae admin_facultades en token y tampoco encontró, casi siempre es scope no resuelto;
    // pero igual devolvemos false.
    return found || (!hasAnyFacInToken ? false : false);
  }, [user]);

  useEffect(() => {
    let abort = false;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError("");

        const role = norm(currentRole);

        // ✅ Admin/Docente pasan sin requerir inscripción
        if (["administrador", "admin", "superadmin", "docente"].includes(role)) {
          if (!abort) {
            setAllowed(true);
            setLoading(false);
          }
          return;
        }

        // ✅ Admin de facultad: pasa SOLO si el curso está en una de sus facultades administradas
        if (role === "admin_facultad") {
          const ok = await canAccessAsFacultyAdmin(codigoCurso, { signal: ctrl.signal });
          if (!abort) {
            setAllowed(!!ok);
            setLoading(false);
            if (!ok) {
              setError(
                "Acceso denegado. Eres admin de facultad, pero este curso no pertenece a tus facultades asignadas."
              );
            }
          }
          return;
        }

        // ✅ Estudiante: requiere inscripción
        const course = await fnsRef.current.resolveStudentCourseByCode(codigoCurso, { signal: ctrl.signal });
        let enrolled = !!course?.id;

        // Fallback por si tu endpoint no valida inscripción
        if (!enrolled && typeof fnsRef.current.listMyCourses === "function") {
          const mine = await fnsRef.current.listMyCourses({ signal: ctrl.signal });
          const arr = Array.isArray(mine?.items) ? mine.items : Array.isArray(mine) ? mine : [];
          enrolled = arr.some(
            (c) => norm(c.codigo_curso || c.codigo || c.code) === norm(codigoCurso)
          );
        }

        if (!abort) {
          setAllowed(enrolled);
          setLoading(false);
          if (!enrolled) setError("No estás inscrito en este curso.");
        }
      } catch (e) {
        if (!abort) {
          setAllowed(false);
          setLoading(false);
          setError(e?.message || "No tienes acceso a este curso.");
        }
      }
    })();

    return () => {
      abort = true;
      ctrl.abort();
    };
  }, [codigoCurso, currentRole, canAccessAsFacultyAdmin]);

  if (loading) return <LoadingSpinner message="Cargando…" />;

  if (!allowed) {
    return (
      <div
        style={{
          maxWidth: 720,
          margin: "40px auto",
          background: "#fff",
          borderRadius: 16,
          padding: 32,
          boxShadow: "0 8px 32px rgba(0,0,0,.08)",
        }}
      >
        <h2 style={{ marginBottom: 8 }}>Acceso denegado</h2>
        <p style={{ marginBottom: 16, color: "#6b7280" }}>{error}</p>
        <div style={{ display: "flex", gap: 12 }}>
          <Link to="/" className="btn btn-primary">
            Ir al inicio
          </Link>
          <button onClick={() => window.history.back()} className="btn">
            Volver
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}